import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import '../styles/asset-card.css';

const AssetCard = ({ asset: basicAsset }) => {
  const navigate = useNavigate();
  const [asset, setAsset] = useState(basicAsset);
  const [showExplanation, setShowExplanation] = useState(false);
  const [, setLoadingDetails] = useState(false);

  useEffect(() => {
    fetchAssetDetails();
  }, [basicAsset.ticker]);

  const fetchAssetDetails = async () => {
    try {
      setLoadingDetails(true);
      const response = await axios.get(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/assets/${basicAsset.ticker}`);
      if (response.data.success) {
        setAsset(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching asset details:', error);
      // Keep using basic asset data if detailed fetch fails
    } finally {
      setLoadingDetails(false);
    }
  };

  const getAssetIcon = () => {
    switch(asset.assetClass) {
      case 'stock': return '📈';
      case 'bond': return '🏦';
      case 'index': return '📊';
      case 'crypto': return '🪙';
      default: return '💼';
    }
  };

  const getLogoUrl = () => {
    // Use backend-provided logo URLs if available
    if (asset.branding?.icon_url) {
      return asset.branding.icon_url;
    }
    if (asset.branding?.logo_url) {
      return asset.branding.logo_url;
    }
    
    // No logo available from backend
    return null;
  };

  const getAssetTypeIcon = () => {
    const ticker = asset.ticker.toUpperCase();
    
    // Special icons for well-known tickers
    const iconMap = {
      'AAPL': '🍎',
      'DIS': '🏰',
      'NFLX': '📺',
      'TSLA': '🚗',
      'MSFT': '💻',
      'GOOGL': '🔍',
      'AMZN': '📦',
      'BTC': '₿',
      'ETH': 'Ξ',
      'SPY': '🇺🇸',
      'QQQ': '💻',
      'BND': '🏛️',
      'AGG': '📊'
    };
    
    return iconMap[ticker] || getAssetIcon();
  };

  const getAssetColor = () => {
    switch(asset.assetClass) {
      case 'stock': return '#10b981';
      case 'bond': return '#3b82f6';
      case 'index': return '#8b5cf6';
      case 'crypto': return '#f59e0b';
      default: return '#6366f1';
    }
  };

  const getRarityStars = () => {
    // Determine rarity based on price and performance
    const price = asset.currentPrice;
    if (price > 500) return '⭐⭐⭐⭐⭐';
    if (price > 200) return '⭐⭐⭐⭐';
    if (price > 100) return '⭐⭐⭐';
    if (price > 50) return '⭐⭐';
    return '⭐';
  };

  const handleExplain = () => {
    setShowExplanation(!showExplanation);
  };

  const getDescription = () => {
    // Use backend description if available (your specified API structure)
    if (asset.description) {
      // Truncate long descriptions for card display
      return asset.description.length > 150 
        ? asset.description.substring(0, 150) + '...'
        : asset.description;
    }
    
    // Fallback descriptions for current backend (until Polygon.io data is integrated)
    const fallbacks = {
      'AAPL': 'Apple Inc. designs, manufactures, and markets smartphones, personal computers, tablets, wearables, and accessories worldwide.',
      'DIS': 'The Walt Disney Company is a leading entertainment company with theme parks, movies, and media networks worldwide.',
      'NFLX': 'Netflix is the world\'s leading streaming entertainment service with over 200 million paid memberships.',
      'BTC': 'Bitcoin is a decentralized digital currency that enables instant payments to anyone, anywhere in the world.'
    };
    
    return fallbacks[asset.ticker] || `${asset.name || asset.ticker} - A ${asset.assetClass} investment opportunity in the financial markets.`;
  };

  const handleBuy = () => {
    navigate(`/buy/${asset.ticker}`, { state: { asset } });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(price);
  };

  const formatChange = (change, percent) => {
    const sign = change >= 0 ? '+' : '';
    return `${sign}${percent.toFixed(2)}%`;
  };

  return (
    <div className="asset-card">
      <div className="card-inner">
        {/* Front of card */}
        <div className="card-front" style={{ '--card-color': getAssetColor() }}>
          {/* Card Header */}
          <div className="card-header">
            <span className="asset-type">{asset.assetClass?.toUpperCase()}</span>
            <span className="asset-icon">{getAssetIcon()}</span>
          </div>

          {/* Card Image Area - Logo/Icon */}
          <div className="card-image-area">
            <div className="asset-logo">
              {getLogoUrl() ? (
                <img 
                  src={getLogoUrl()} 
                  alt={`${asset.ticker} logo`}
                  className="company-logo"
                  onError={(e) => {
                    // Fallback to icon if logo fails to load
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : null}
              <div className="logo-fallback" style={{display: getLogoUrl() ? 'none' : 'flex'}}>
                <span className="asset-type-icon">{getAssetTypeIcon()}</span>
                <span className="ticker-symbol">{asset.ticker}</span>
              </div>
            </div>
            <div className="sparkle-effects">
              <span className="sparkle sparkle-1">✨</span>
              <span className="sparkle sparkle-2">✨</span>
              <span className="sparkle sparkle-3">✨</span>
            </div>
          </div>

          {/* Asset Name */}
          <div className="asset-name-section">
            <h3 className="asset-ticker">{asset.ticker}</h3>
            <p className="asset-name">{asset.name || asset.ticker}</p>
          </div>

          {/* Price Info Box */}
          <div className="price-info-box">
            <div className="price-main">
              <span className="price-label">Price</span>
              <span className="price-value">{formatPrice(asset.currentPrice)}</span>
            </div>
            <div className={`price-change ${asset.todaysChange >= 0 ? 'positive' : 'negative'}`}>
              <span className="change-value">
                {formatChange(asset.todaysChange, asset.todaysChangePerc)}
              </span>
              <span className="change-arrow">
                {asset.todaysChange >= 0 ? '📈' : '📉'}
              </span>
            </div>
          </div>

          {/* Stats */}
          <div className="asset-stats">
            <div className="stat-item">
              <span className="stat-label">Rarity</span>
              <span className="stat-value">{getRarityStars()}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Power</span>
              <span className="stat-value">
                {Math.abs(asset.todaysChangePerc * 10).toFixed(0)}/100
              </span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="card-actions">
            <button 
              className="explain-button"
              onClick={handleExplain}
            >
              🤔 {showExplanation ? 'Hide Info' : 'What\'s this?'}
            </button>
            <button 
              className="buy-button"
              onClick={handleBuy}
            >
              💰 Buy Now
            </button>
          </div>

          {/* Card Footer - Description */}
          {showExplanation && (
            <div className="card-description">
              <div className="description-content">
                <p>{getDescription()}</p>
                
                {/* Company metrics (ready for your API structure) */}
                <div className="asset-metrics-expanded">
                  {asset.market_cap && (
                    <div className="metric-item">
                      <span className="metric-label">Market Cap:</span>
                      <span className="metric-value">${(asset.market_cap / 1e9).toFixed(1)}B</span>
                    </div>
                  )}
                  {asset.total_employees && (
                    <div className="metric-item">
                      <span className="metric-label">Employees:</span>
                      <span className="metric-value">{asset.total_employees.toLocaleString()}</span>
                    </div>
                  )}
                  {asset.sic_description && (
                    <div className="metric-item">
                      <span className="metric-label">Industry:</span>
                      <span className="metric-value">{asset.sic_description}</span>
                    </div>
                  )}
                </div>
                
                {/* Company website link (ready for your API structure) */}
                {asset.homepage_url && (
                  <a 
                    href={asset.homepage_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="company-website"
                  >
                    🌐 Visit Website
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Holographic Effect */}
          <div className="holographic-overlay"></div>
        </div>

        {/* Back of card (for future use) */}
        <div className="card-back">
          <div className="card-back-pattern"></div>
          <div className="card-back-logo">💰</div>
        </div>
      </div>
    </div>
  );
};

export default AssetCard;